// screens-play.tsx - Public play surface: hosted game landing template + runtime
// Lobbies are game UI; each game ships its own. The platform provides landing + runtime.
// Live publications come from GET /play/{slug}. A publication with
// external_url set (e.g. a bespoke client that outgrew this generic
// template) redirects to its own dedicated site instead of rendering here -
// a property of the publication itself, not a hardcoded slug list.
// Exports: ScreenGameLanding, ScreenPlayRuntime, PlayTopBar, LandingLeaderboard,
//          deriveLandingView, prettySlug
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AudioEngine,
  ClientLogicRuntime,
  GameRuntime,
  createDefaultProjectConfig,
  registerCorePack,
  useEngineRuntimeStore,
  type ClientLogicRuntimeHandle,
  type MapDocument,
  type PlatformerConfig,
  type StudioAssetRecord,
  type StudioProjectConfig,
} from '../../engine';
import { registerProjectAssets } from '../assets/registerProjectAsset';
import { GeneratedCover } from '../components/GeneratedCover';
import { ImageSlot } from '../components/ImageSlot';
import { Glyph, NotFoundScreen } from '../components/ui-shared';
import { Link } from '../design/router';
import {
  AUDIO_DOC_FALLBACK,
  HUD_DOC_FALLBACK,
  LOGIC_DOC_FALLBACK,
  migrateAudioDoc,
  type AudioDoc,
  type HudDoc,
  type LogicDoc,
} from '../editor/editor-modes2';
import { GAMEDATA_DOC_FALLBACK, type GameDataDoc } from '../editor/editor-data-fx';
import { CUTSCENE_LIBRARY_DOC_FALLBACK, type CutsceneLibraryDoc } from '../editor/CutsceneTimeline';
import { HudOverlayView, useAudioPlayback, useHudPlayState } from '../editor/screens-editor';
import { assetFromWire, charactersFromRoster, poseLibrariesFromSavedPoses } from '../persistence/projectRepository';
import { CopyField } from '../project/screens-project';
import { API_URL, ApiHttpError } from '../services/api';
import { cameraModeFromProjection, GAMESETUP_DOC_FALLBACK, type GameSetupDoc } from '../services/gameSetup';
import {
  getPlay,
  getPlayAssets,
  getPlayDoc,
  getPlayLeaderboard,
  isPlayableMap,
  redirectToExternalPlay,
  type LeaderboardRow,
  type PlayPayload,
} from './playClient';
import { PlayScriptConsole } from './PlayScriptConsole';
import { usePlayScriptConsoleStore } from './playScriptConsoleStore';
import { usePlayScriptRuntime } from './usePlayScriptRuntime';
import { TIER2_SCRIPTING_ENABLED } from '../config/tier2ScriptingFeature';
import { PresenceList } from './relay/PresenceList';
import { getCachedPlayerId } from './relay/playerSession';
import { useRelayConnection } from './relay/useRelayConnection';
import { useMultiplayerSync } from './relay/useMultiplayerSync';

/** A structurally valid, empty map: the `useHudPlayState` hook needs a
 * MapDocument on every render, including before the real one has loaded.
 * Module-level so it is referentially stable across renders. */
const EMPTY_PLAY_MAP: MapDocument = {
  id: 'play-empty',
  name: '',
  version: 3,
  floors: [],
  objectInstances: [],
  stairTriggers: [],
};

/* ── Landing view model ─────────────────────────────────────────────────── */

export interface LandingView {
  name: string;
  eyebrow: string;
  creator: string;
  /** Public dev-profile slug when known; renders the creator as a link. */
  creatorSlug: string | null;
  blurb: string;
  stats: { n: string; l: string }[];
  facts: { label: string; value: string }[];
  shareSlug: string;
  /** The creator's uploaded key art; null falls back to a generated cover. */
  coverUrl: string | null;
  /** The project's public wiki, when it has one - never set for an unlisted
   * or private wiki (see the api). */
  wiki: { title: string; url: string } | null;
}

/** Matches the `theme_color`/`background_color` served by SF18.1's
 * `GET /play/{slug}/manifest.json` - the installed app's OS chrome tint. */
const PLAY_THEME_COLOR = '#0b0b10';

/** Installability for a published game: the per-slug manifest link, a
 * matching theme-color meta tag, and the minimal play-surface service
 * worker, scoped to this slug only. Play routes only - never runs in the
 * Studio editor - and tears its own tags down on unmount so navigating
 * away never leaves another game's (or no game's) manifest linked. */
function usePlayInstallability(slug: string) {
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'manifest';
    // SF18.1's manifest endpoint lives on the api host, a different origin
    // than this SPA (see API_URL/getPlay) - a same-origin-relative href would
    // 404 in production. The manifest itself may be fetched cross-origin
    // (already CORS-allowed, same as every other /play/* call this screen
    // makes); a service worker script may not, so that stays same-origin below.
    link.href = `${API_URL}/play/${slug}/manifest.json`;
    document.head.appendChild(link);

    const theme = document.createElement('meta');
    theme.name = 'theme-color';
    theme.content = PLAY_THEME_COLOR;
    document.head.appendChild(theme);

    // Registration persists independently of this component's lifecycle (a
    // service worker is meant to outlive the page); only the tags above are
    // torn down on unmount.
    if (navigator.serviceWorker) {
      navigator.serviceWorker.register('/play/sw.js', { scope: `/play/${slug}/` }).catch(() => {});
    }

    return () => {
      link.remove();
      theme.remove();
    };
  }, [slug]);
}

/** Title-case a publication slug for games the api gives no name for yet. */
export function prettySlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ');
}

/** Map a live publication onto the landing template. Optional api fields
 * (name, updated_at, dev) are used when present and omitted honestly when not. */
export function deriveLandingView(p: PlayPayload): LandingView {
  const updated = p.updated_at
    ? new Date(p.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  return {
    name: p.name || prettySlug(p.slug),
    eyebrow: 'Made with Soulstack · Free',
    creator: p.dev?.studio_name || 'a Soulstack creator',
    creatorSlug: p.dev?.slug ?? null,
    blurb: 'Built in the browser with Soulstack Studio and hosted on the platform it was made on.',
    stats: [
      { n: 'v' + p.version, l: 'Current version' },
      { n: 'Browser', l: 'Runs in' },
      { n: 'Free', l: 'To play' },
    ],
    facts: [
      { label: 'Visibility', value: p.visibility === 'unlisted' ? 'Unlisted' : 'Public' },
      ...(updated ? [{ label: 'Updated', value: updated }] : []),
      { label: 'Region', value: 'EU West' },
    ],
    shareSlug: p.slug,
    coverUrl: p.config?.cover?.url ?? null,
    wiki: p.wiki ? { title: p.wiki.title, url: p.wiki.url } : null,
  };
}

/* ── Lobby leaderboard (player-facing, on the landing page) ────────────── */
const BOARD_COLUMNS = '44px 1fr 90px 70px 110px 90px';

/**
 * The landing page lobby board. Binds rows from `GET /play/{slug}/leaderboard`;
 * an empty array renders the deliberate empty state shown until a game
 * records its first match.
 */
export function LandingLeaderboard({ rows }: { rows: LeaderboardRow[] }) {
  return (
    <div style={{ marginBottom: 48 }}>
      <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 16 }}>Leaderboard</h2>
      {rows.length === 0 ? (
        <div className="ss-card" style={{ padding: '30px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>No matches yet</span>
          <span style={{ fontSize: 12.5, color: 'var(--ss-text-3)' }}>Scores appear here after the first match.</span>
        </div>
      ) : (
        // A 6-column data table doesn't fit a phone width at any font size
        // worth reading; it scrolls horizontally within its own card instead
        // of clipping columns off unreachably or crushing them illegibly.
        <div className="ss-card" style={{ padding: '4px 0', overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: BOARD_COLUMNS, gap: 12, padding: '12px 20px 10px', minWidth: 460, fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>
            <span>Rank</span>
            <span>Player</span>
            <span style={{ textAlign: 'right' }}>Matches</span>
            <span style={{ textAlign: 'right' }}>Wins</span>
            <span style={{ textAlign: 'right' }}>K / D / A</span>
            <span style={{ textAlign: 'right' }}>Score</span>
          </div>
          {rows.map((r, i) => (
            <div key={r.player_ref} style={{ display: 'grid', gridTemplateColumns: BOARD_COLUMNS, gap: 12, minWidth: 460, alignItems: 'baseline', padding: '10px 20px', borderTop: '1px solid var(--ss-border-soft)' }}>
              <span style={{ fontFamily: 'var(--ss-mono)', fontSize: 12, color: 'var(--ss-text-3)' }}>{String(i + 1).padStart(2, '0')}</span>
              <span style={{ fontSize: 13.5, fontWeight: 600 }}>{r.name || 'Player ' + r.player_ref.slice(0, 6)}</span>
              <span style={{ fontFamily: 'var(--ss-mono)', fontSize: 12.5, color: 'var(--ss-text-2)', textAlign: 'right' }}>{r.matches.toLocaleString('en-GB')}</span>
              <span style={{ fontFamily: 'var(--ss-mono)', fontSize: 12.5, color: 'var(--ss-text-2)', textAlign: 'right' }}>{r.wins.toLocaleString('en-GB')}</span>
              <span style={{ fontFamily: 'var(--ss-mono)', fontSize: 12.5, color: 'var(--ss-text-2)', textAlign: 'right' }}>{r.kills.toLocaleString('en-GB')} / {r.deaths.toLocaleString('en-GB')} / {r.assists.toLocaleString('en-GB')}</span>
              <span style={{ fontFamily: 'var(--ss-mono)', fontSize: 12.5, color: 'var(--ss-accent-strong)', textAlign: 'right' }}>{r.score.toLocaleString('en-GB')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PlayTopBar() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 28px', height: 64, borderBottom: '1px solid var(--ss-border-soft)',
      background: 'var(--ss-glass)', backdropFilter: 'blur(14px)', position: 'sticky', top: 0, zIndex: 50,
    }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Glyph size={24} />
        <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.18em', color: 'var(--ss-text-3)', textTransform: 'uppercase' }}>sstk.studio / play</span>
      </Link>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Link to="/showcase" className="mk-nav-link" style={{ fontSize: 13 }}>More games</Link>
        <Link to="/signup" className="ss-btn ss-btn--ghost ss-btn--sm">Build your own</Link>
      </div>
    </div>
  );
}

/** Bare chrome while a publication loads; avoids flashing the wrong game. */
function PlayShell() {
  return (
    <div className="ss-screen">
      <PlayTopBar />
    </div>
  );
}

/** Honest not-found for unknown or private slugs. */
function PlayNotFound() {
  return (
    <div className="ss-screen" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PlayTopBar />
      <NotFoundScreen
        style={{ flex: 1 }}
        title="No game here."
        body="This link does not match a public game. It may have been unpublished, set to private, or never existed."
        ctas={[
          { label: 'Browse games', to: '/showcase' },
          { label: 'Build your own', to: '/signup', variant: 'ghost' },
        ]}
      />
    </div>
  );
}

/* ── Published-game landing (template every hosted game gets) ─────────── */
export function ScreenGameLanding({ slug }: { slug: string }) {
  usePlayInstallability(slug);
  // Live publications come from the api. An unknown slug is a not-found
  // page, never someone else's game.
  const [real, setReal] = useState<PlayPayload | null>(null);
  const [board, setBoard] = useState<LeaderboardRow[] | null>(null);
  const [settled, setSettled] = useState(false);
  // 'not-found' (404 or private, per api/routers/public.py both answer 404
  // either way) gets the honest not-found page; 'error' is a real network/
  // server failure and gets its own message instead of being folded into
  // the same generic bucket - a bad or private play link was previously
  // indistinguishable from the server being down.
  const [loadError, setLoadError] = useState<'not-found' | 'error' | null>(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReal(null);
    setBoard(null);
    setSettled(false);
    setLoadError(null);
    setRedirecting(false);
    getPlay(slug)
      .then((p) => {
        if (cancelled) return;
        if (p.external_url) {
          setRedirecting(true);
          void redirectToExternalPlay(slug, p.external_url);
          return;
        }
        setReal(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiHttpError && err.status === 404 ? 'not-found' : 'error');
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    getPlayLeaderboard(slug)
      .then((rows) => {
        if (!cancelled) setBoard(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (redirecting) return <PlayShell />;

  if (!real) {
    if (!settled) return <PlayShell />;
    if (loadError === 'error') return (
      <div className="ss-screen" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <p style={{ fontSize: 15, color: 'var(--ss-text-2)', marginBottom: 12 }}>Failed to load game</p>
          <p style={{ fontSize: 13, color: 'var(--ss-text-3)', lineHeight: 1.6 }}>Could not reach the server. Check your connection and refresh.</p>
        </div>
      </div>
    );
    return <PlayNotFound />;
  }
  const v = deriveLandingView(real);
  // Empty state until the game records its first match.
  const rows = board ?? [];
  return (
    <div className="ss-screen">
      <PlayTopBar />
      <header style={{ position: 'relative', height: 460, overflow: 'hidden' }}>
        {v.coverUrl ? (
          <ImageSlot shape="rect" src={v.coverUrl} alt={v.name + ' key art'}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />
        ) : (
          <GeneratedCover name={v.name} fontSize={150}
            style={{ position: 'absolute', inset: 0, height: '100%', borderRadius: 0, border: 'none' }} />
        )}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'linear-gradient(180deg, rgba(6,7,13,0.1) 30%, rgba(6,7,13,0.96) 92%)' }} />
      </header>
      <div style={{ maxWidth: 980, margin: '-120px auto 0', padding: '0 40px 80px', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, marginBottom: 36 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span className="ss-eyebrow">{v.eyebrow}</span>
            <h1 className="ss-display" style={{ fontSize: 'clamp(2.1rem, 9vw, 3.5rem)' }}>{v.name}</h1>
            <span style={{ fontSize: 14, color: 'var(--ss-text-3)' }}>
              by{' '}
              {v.creatorSlug ? (
                <Link to={'/dev/' + v.creatorSlug} style={{ color: 'var(--ss-text-2)', fontWeight: 600 }}>{v.creator}</Link>
              ) : (
                <strong style={{ color: 'var(--ss-text-2)', fontWeight: 600 }}>{v.creator}</strong>
              )}
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12, paddingBottom: 6 }}>
            <Link to={'/play/' + slug + '/live'} className="ss-btn ss-btn--primary ss-btn--lg" style={{ minWidth: 200, justifyContent: 'center' }}>Play now</Link>
            <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--ss-text-3)' }}>PLAYS IN THIS TAB · INSTALLABLE</span>
          </div>
        </div>

        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 40 }}>
          {v.stats.map((s) => (
            <div key={s.l} className="stat-card">
              <span className="n">{s.n}</span>
              <span className="l">{s.l}</span>
            </div>
          ))}
        </div>

        <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 40, marginBottom: 48 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h2 style={{ fontSize: 17, fontWeight: 600 }}>About</h2>
            <p style={{ color: 'var(--ss-text-2)', fontSize: 15.5, lineHeight: 1.7, textWrap: 'pretty' }}>{v.blurb}</p>
            <div style={{ display: 'flex', gap: 22, fontSize: 12.5, color: 'var(--ss-text-3)', marginTop: 6 }}>
              {v.facts.map((f) => (
                <span key={f.label}>{f.label} <strong style={{ color: 'var(--ss-text-2)', fontWeight: 500 }}>{f.value}</strong></span>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="set-card">
              <h3>Share</h3>
              {/* window.location.host, not a hardcoded 'sstk.studio' - found during
                  the play-origin-isolation audit (docs/editor-consolidation/
                  play-origin-isolation.md): a hardcoded production domain here would
                  hand out a wrong, stale share link the moment /play/<slug> is served
                  from a different host than this string assumes, matching whatever
                  host actually served this page instead - correct both before and
                  after the origin split, and already correct for local dev, unlike
                  the string it replaced. */}
              <CopyField value={window.location.host + '/play/' + v.shareSlug} />
              <span className="hint">Anyone with the link plays instantly. No account needed. Add it to a home screen or install it from the browser to play it as an app.</span>
            </div>
            {v.wiki && (
              <div className="set-card">
                <h3>Wiki</h3>
                <span className="hint">Lore, guides and reference pages for this game.</span>
                <a className="ss-btn ss-btn--ghost ss-btn--sm" href={v.wiki.url} target="_blank" rel="noreferrer" style={{ alignSelf: 'flex-start' }}>
                  Read the {v.wiki.title}
                </a>
              </div>
            )}
          </div>
        </div>

        <LandingLeaderboard rows={rows} />
      </div>
      <footer style={{ borderTop: '1px solid var(--ss-border-soft)', padding: '36px 40px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <Glyph size={18} />
        <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ss-text-3)' }}>
          Built and hosted on Soulstack Studio
        </span>
        <Link to="/signup" style={{ fontSize: 12.5, color: 'var(--ss-accent)', fontWeight: 600, marginLeft: 8 }}>Start building</Link>
      </footer>
    </div>
  );
}

/* ── Runtime play page ────────────────────────────────────────────────── */
export function ScreenPlayRuntime({ slug }: { slug: string }) {
  usePlayInstallability(slug);
  const [payload, setPayload] = useState<PlayPayload | null>(null);
  const [settled, setSettled] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  // A stable object identity, not a fresh `{ onLockChange: setLocked }`
  // literal recreated every render: `GameRuntime`'s `callbacks` prop feeds
  // its own `playerCallbacks` useMemo, and an unstable `callbacks` reference
  // here defeated that memo on every render regardless of whether
  // `setLocked` (a stable setState identity) itself ever changed - one link
  // in the chain that ultimately re-ran `FirstPersonController`'s keyboard-
  // listener effect and silently dropped a currently-held movement key (see
  // `useMultiplayerSync.ts`'s own `onSharedEffect` doc comment for the full
  // chain this is one part of).
  const gameRuntimeCallbacks = useMemo(() => ({ onLockChange: setLocked }), []);
  const [pct, setPct] = useState(0);
  const [redirecting, setRedirecting] = useState(false);
  // Uploaded assets and the HUD/audio/game-data mode documents: the same
  // things editor Preview loads before it plays a map (registerProjectAssets,
  // usePreviewHud, usePreviewAudio in screens-editor.tsx). A published game
  // must render what Preview showed the creator, not a thinner cut of it.
  const [assets, setAssets] = useState<StudioAssetRecord[]>([]);
  const [hudDoc, setHudDoc] = useState<HudDoc>(HUD_DOC_FALLBACK);
  const [gameDataDoc, setGameDataDoc] = useState<GameDataDoc>(GAMEDATA_DOC_FALLBACK);
  const [audioDoc, setAudioDoc] = useState<AudioDoc>(AUDIO_DOC_FALLBACK);
  const [logicDoc, setLogicDoc] = useState<LogicDoc>(LOGIC_DOC_FALLBACK);
  const [cutsceneDoc, setCutsceneDoc] = useState<CutsceneLibraryDoc>(CUTSCENE_LIBRARY_DOC_FALLBACK);
  const logicRuntimeRef = useRef<ClientLogicRuntimeHandle | null>(null);
  const setCameraModeForced = useEngineRuntimeStore((s) => s.setCameraModeForced);
  // Game Setup's gravity field, read alongside its projection/camera choice
  // below - previously saved and never read by any runtime. `undefined`
  // (no gamesetup doc, or an unparseable value) leaves Platformer2DScene on
  // its own built-in default rather than forcing a value on every game.
  const [platformerConfig, setPlatformerConfig] = useState<Partial<PlatformerConfig> | undefined>(undefined);
  // Tier-2 scripting (docs/editor-consolidation/tier2-quickjs-sandbox.md):
  // the same GameSetupDoc.scriptCapabilities grant tier 1 reads in Preview,
  // captured here from the same gamesetup doc fetch already below rather
  // than a second request. `undefined` until that fetch resolves - matches
  // GameSetupDoc's own "not configured" meaning (grant every v1 capability),
  // narrowed at run time by PlayScriptConsole via narrowScriptCapabilities.
  const [scriptCapabilities, setScriptCapabilities] = useState<readonly string[] | undefined>(undefined);
  // GameSetupDoc's player-character picker (GameRulesPanel), captured from
  // the same gamesetup doc fetch below - resolved against the roster by
  // GameRuntime's own resolvePlayerCharacter, same "undefined keeps the
  // stock rig" default every other optional field on this doc has.
  const [playerCharacterId, setPlayerCharacterId] = useState<string | undefined>(undefined);
  const scriptLogicRef = useRef<ClientLogicRuntime | null>(null);
  const audioEngineRef = useRef<AudioEngine | null>(null);
  const onAudioEngineReady = useCallback((engine: AudioEngine) => { audioEngineRef.current = engine; }, []);
  const onAudioEngineDispose = useCallback(() => { audioEngineRef.current = null; }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    getPlay(slug)
      .then(async (p) => {
        if (cancelled) return;
        if (p.external_url) {
          setRedirecting(true);
          void redirectToExternalPlay(slug, p.external_url);
          return;
        }
        // A publication whose map fails validation cannot be rendered; surface a
        // real error instead of falling through to the "no game here" not-found.
        if (!isPlayableMap(p.map_data)) {
          setLoadError('This game’s map data is invalid or incomplete and cannot be played.');
          return;
        }
        registerCorePack();
        // Placed library models need their definitions on the play surface;
        // only games that actually reference the library pay for the
        // manifest fetch. Fail-soft like everywhere else.
        const map = p.map_data;
        const usesLibrary = (map.objectInstances ?? []).some((i) => i.assetId?.startsWith('library:'));
        const tasks: Promise<unknown>[] = [];
        if (usesLibrary) {
          tasks.push(import('../assets/libraryLoader').then(({ ensureLibraryLoaded }) => ensureLibraryLoaded()));
        }
        // Uploaded assets must be registered before GameRuntime mounts (same
        // ordering ProjectProvider uses for the editor) so the first frame
        // never renders a placed model before its definition exists.
        tasks.push(
          getPlayAssets(slug)
            .then((rows) => rows.map(assetFromWire))
            .catch(() => [] as StudioAssetRecord[])
            .then((records) => {
              registerProjectAssets(records);
              if (!cancelled) setAssets(records);
            }),
        );
        tasks.push(
          getPlayDoc<HudDoc>(slug, 'hud')
            .then((doc) => {
              if (!cancelled && doc.data && Object.keys(doc.data).length > 0) setHudDoc(doc.data);
            })
            .catch(() => {}),
        );
        tasks.push(
          getPlayDoc<GameDataDoc>(slug, 'gamedata')
            .then((doc) => {
              if (!cancelled && doc.data && Object.keys(doc.data).length > 0) setGameDataDoc(doc.data);
            })
            .catch(() => {}),
        );
        tasks.push(
          getPlayDoc<LogicDoc>(slug, 'logic')
            .then((doc) => {
              if (!cancelled && doc.data && Object.keys(doc.data).length > 0) setLogicDoc(doc.data);
            })
            .catch(() => {}),
        );
        tasks.push(
          getPlayDoc(slug, 'audio')
            .then((doc) => {
              if (!cancelled) setAudioDoc(migrateAudioDoc(doc.data));
            })
            .catch(() => {}),
        );
        tasks.push(
          getPlayDoc<CutsceneLibraryDoc>(slug, 'cutscenes')
            .then((doc) => {
              if (!cancelled && doc.data && Object.keys(doc.data).length > 0) setCutsceneDoc(doc.data);
            })
            .catch(() => {}),
        );
        // A creator's forced camera choice (editor-panels.tsx's GameRoute,
        // SF20.2) must reach a real player too, not just the editor's own
        // Preview - that only pushed it into the same-tab zustand store,
        // which a published /play/:slug mount never shares. Set it before
        // `payload` flips (below), which is what mounts GameRuntime ->
        // FirstPersonController; no cleanup on unmount needed, matching
        // GameRoute's own "overwritten fresh on next mount" design (see its
        // comment) - the next thing to mount, whatever it is, sets its own
        // value before it ever reads this one.
        tasks.push(
          getPlayDoc<GameSetupDoc>(slug, 'gamesetup')
            .then((doc) => {
              if (cancelled) return;
              const projection = doc.data?.projection ?? GAMESETUP_DOC_FALLBACK.projection;
              setCameraModeForced(cameraModeFromProjection(projection));
              const gravity = Number(doc.data?.gravity);
              setPlatformerConfig(Number.isFinite(gravity) ? { gravity } : undefined);
              // Task item 4's per-project capability grant, same field tier
              // 1 reads for Preview - captured for tier 2's PlayScriptConsole
              // even when TIER2_SCRIPTING_ENABLED is off, since reading one
              // more field off a doc already being fetched is free; only the
              // console itself, and usePlayScriptRuntime's actual sandbox
              // wiring, are gated on that flag.
              setScriptCapabilities(doc.data?.scriptCapabilities);
              setPlayerCharacterId(doc.data?.playerCharacterId);
            })
            .catch(() => {}),
        );
        await Promise.all(tasks);
        if (cancelled) return;
        setPayload(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('[ScreenPlayRuntime] failed to load publication:', err);
        // A missing/private slug (404) falls through to the honest not-found
        // page below instead of surfacing the raw "API GET ... 404" string as
        // if it were a real failure.
        if (err instanceof ApiHttpError && err.status === 404) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load the game.');
      })
      .finally(() => {
        if (!cancelled) setSettled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, setCameraModeForced]);

  // Streaming progress: advances while the publication downloads, holds just
  // short of done until the fetch settles, then completes.
  useEffect(() => {
    if (pct >= 100) return;
    const ceiling = settled ? 100 : 92;
    const t = setTimeout(() => setPct((v) => Math.min(ceiling, v + Math.ceil(Math.random() * 18))), 180);
    return () => clearTimeout(t);
  }, [pct, settled]);
  const loaded = settled && pct >= 100;

  const name = payload ? payload.name || prettySlug(payload.slug) : prettySlug(slug);
  const version = payload ? 'v' + payload.version : '';
  const config = useMemo<StudioProjectConfig>(
    () => ({ ...createDefaultProjectConfig(), ...(payload?.config ?? {}) }),
    [payload],
  );
  // Placed characters/NPC patrols (LevelRenderer -> PlacedCharacters) resolve
  // against these the same way Studio's own Edit/Preview does via
  // `loadProject` - built-in neutral biped plus the project's own roster and
  // saved poses, now that `getPlay` actually carries them.
  const characters = useMemo(() => charactersFromRoster(payload?.characters), [payload]);
  const poseLibraries = useMemo(() => poseLibrariesFromSavedPoses(payload?.poses), [payload]);

  // Live HUD + positional audio, mirroring editor Preview exactly (same
  // hooks, same combined per-frame position callback). Called unconditionally
  // (rules of hooks) against a stable empty map/asset list before the real
  // publication has loaded.
  const map = payload?.map_data ?? EMPTY_PLAY_MAP;
  const triggers = useMemo(() => map.triggers ?? [], [map]);
  const markers = useMemo(() => map.markers ?? [], [map]);
  const is2dProjection = config.projection === '2D';
  const { live, onPosition: onHudPosition, onLogicEffect } = useHudPlayState(map, triggers, markers, is2dProjection, logicRuntimeRef);
  const onAudioPosition = useAudioPlayback(assets, audioDoc, onAudioEngineReady, onAudioEngineDispose);
  // Multiplayer relay presence (design.md section 6): a no-op until
  // payload.game_id resolves, and additive even then - a published game
  // renders and plays identically whether or not the relay is reachable.
  const {
    presence: relayPresence,
    status: relayStatus,
    messages: relayMessages,
    consumeMessages: consumeRelayMessages,
    initialState: relayInitialState,
    send: relaySend,
  } = useRelayConnection(payload?.game_id);
  // The real relay-issued player id for the local player, once the relay
  // connection is actually open - `playerSession.ts` caches it during the
  // guest signup `fetchRelayToken` already ran before the socket opened, so
  // it is available synchronously here (see `ClientLogicRuntime`'s Phase 0
  // fix, roadmap.md section 5). `undefined` (relay not open) leaves
  // `GameRuntime`/`ClientLogicRuntime` on their own single-player fallback.
  const localPlayerId = relayStatus === 'open' ? getCachedPlayerId(payload?.game_id) ?? undefined : undefined;
  // The real connected-player count from relay presence (which always
  // includes the local player's own entry once connected, see
  // `useRelayConnection`). Falls back to 1 (just the local player) before a
  // relay connection exists or while it is empty/reconnecting.
  const playerCount = relayPresence.length > 0 ? relayPresence.length : 1;
  // The sync engine proper (docs/multiplayer-sync/roadmap.md Phases 2-3):
  // host election, transform broadcast/interpolation, and shared Logic
  // state sync, all wired against this same session's scriptLogicRef -
  // exactly the ref usePlayScriptRuntime already writes through for a
  // different reason (the HollowScript scripting capability layer).
  const { isHost, remotePlayerIds, interpolator, reportLocalTransform, onSharedEffect } = useMultiplayerSync({
    gameId: payload?.game_id,
    relayStatus,
    relayPresence,
    localPlayerId,
    messages: relayMessages,
    consumeMessages: consumeRelayMessages,
    initialState: relayInitialState,
    send: relaySend,
    logicRuntimeRef: scriptLogicRef,
  });
  const onPlayerPosition = useCallback(
    (x: number, y: number, z: number) => {
      onAudioPosition(x, y, z);
      onHudPosition(x, y, z);
    },
    [onAudioPosition, onHudPosition],
  );
  // Tier 2's first scripting hook on this route (docs/editor-consolidation/
  // tier2-quickjs-sandbox.md's "What's left" item 1) - a real no-op unless
  // TIER2_SCRIPTING_ENABLED, see usePlayScriptRuntime's own doc comment.
  usePlayScriptRuntime({ scriptLogicRef, audioEngineRef }, scriptCapabilities);
  // Same backtick shortcut PreviewRoute's own console overlay uses
  // (screens-editor.tsx's toggleConsole), scoped to this route's own
  // playScriptConsoleStore. Only registered at all when the feature flag is
  // on - a disabled play session gets no extra keydown listener, not just an
  // overlay that never opens.
  useEffect(() => {
    if (!TIER2_SCRIPTING_ENABLED) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = document.activeElement;
      const editable = target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
      if (event.key !== '`' || editable) return;
      event.preventDefault();
      usePlayScriptConsoleStore.getState().toggleOpen();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (redirecting) return <PlayShell />;

  // Unknown slug: an honest not-found, never a borrowed placeholder.
  if (settled && !payload) {
    if (loadError) {
      return (
        <div className="ss-screen" style={{ height: '100vh', position: 'relative', overflow: 'hidden', background: 'var(--ss-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', padding: '0 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <Glyph size={40} />
            <h1 className="ss-display" style={{ fontSize: 28 }}>Could not load game</h1>
            <p style={{ fontSize: 13.5, color: 'var(--ss-text-3)', maxWidth: 360, lineHeight: 1.6 }}>{loadError}</p>
            <Link to={'/play/' + slug} className="ss-btn ss-btn--ghost" style={{ marginTop: 8 }}>Back to game page</Link>
          </div>
        </div>
      );
    }
    return <PlayNotFound />;
  }

  return (
    <div className="ss-screen" style={{ height: '100vh', position: 'relative', overflow: 'hidden', background: 'var(--ss-bg)' }}>
      {payload ? (
        <>
          <GameRuntime
            // payload is only ever set once isPlayableMap(p.map_data) held, and
            // external publications never reach setPayload at all - map_data is
            // always present here despite the wire type being optional.
            mapDocument={payload.map_data!}
            config={config}
            characters={characters}
            poseLibraries={poseLibraries}
            platformerConfig={platformerConfig}
            playerCharacterId={playerCharacterId}
            mode="play"
            projection={is2dProjection ? '2D' : '3D'}
            callbacks={gameRuntimeCallbacks}
            onPlayerPosition={onPlayerPosition}
            onPlayerTransform={reportLocalTransform}
            logicDocument={logicDoc}
            onLogicEffect={onLogicEffect}
            logicRuntimeRef={logicRuntimeRef}
            scriptLogicRef={scriptLogicRef}
            cutscenes={cutsceneDoc.cutscenes}
            localPlayerId={localPlayerId}
            playerCount={playerCount}
            isHost={isHost}
            onSharedEffect={onSharedEffect}
            remotePlayerIds={remotePlayerIds}
            remoteInterpolator={interpolator}
          />
          <HudOverlayView hud={hudDoc} gameData={gameDataDoc} live={live} />
          <PlayScriptConsole />
        </>
      ) : (
        <GeneratedCover name={name} fontSize={150}
          style={{ position: 'absolute', inset: 0, height: '100%', borderRadius: 0, border: 'none' }} />
      )}
      <span style={{
        position: 'absolute', top: 14, left: 14, display: 'inline-flex', alignItems: 'center', gap: 8,
        background: 'rgba(6,6,8,0.72)', backdropFilter: 'blur(8px)', border: '1px solid var(--ss-border-soft)',
        borderRadius: 999, padding: '7px 14px', fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.08em', color: 'var(--ss-text-2)',
      }}>{name} · {version}</span>
      {payload && <PresenceList presence={relayPresence} />}
      <Link to={'/play/' + slug} style={{
        position: 'absolute', top: 14, right: 14,
        background: 'rgba(6,6,8,0.72)', backdropFilter: 'blur(8px)', border: '1px solid var(--ss-border-soft)',
        borderRadius: 999, padding: '7px 14px', fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.08em', color: 'var(--ss-text-3)',
      }}>EXIT</Link>
      {!loaded && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,4,5,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 340, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22, textAlign: 'center' }}>
            <Glyph size={44} />
            <div>
              <h1 className="ss-display" style={{ fontSize: 26 }}>{name}</h1>
              <p style={{ fontSize: 12.5, color: 'var(--ss-text-3)', marginTop: 6, fontFamily: 'var(--ss-sans)', fontWeight: 500, letterSpacing: '0.08em' }}>STREAMING ASSETS · {pct}%</p>
            </div>
            <div style={{ width: '100%', height: 3, borderRadius: 999, background: 'rgba(255,255,255,0.1)' }}>
              <div style={{ width: pct + '%', height: '100%', borderRadius: 999, background: 'var(--ss-accent)', transition: 'width 180ms ease' }} />
            </div>
          </div>
        </div>
      )}
      {loaded && !locked && (
        <div style={{ position: 'absolute', bottom: 18, left: '50%', transform: 'translateX(-50%)', display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(6,6,8,0.72)', backdropFilter: 'blur(8px)', border: '1px solid var(--ss-border-soft)', borderRadius: 999, padding: '8px 16px', fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10.5, letterSpacing: '0.08em', color: 'var(--ss-text-2)' }}>
          {config.projection === '2D' ? 'WASD OR ARROW KEYS TO MOVE' : 'CLICK TO CAPTURE MOUSE · ESC TO RELEASE'}
        </div>
      )}
    </div>
  );
}
